/**
 * Copyright (C) 2026 Guido Berhoerster <guido+voxit@berhoerster.name>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import * as voxitAdminWebClient from "./voxit-admin-web-client.js";
import { VoxitStatementEditor, VoxitAdminFrontend } from "./voxit-admin-frontend.js";

function mergeSearchParams(params1, params2, replace = true) {
    const params = new URLSearchParams(params1)
    for (const [key, value] of params2.entries()) {
        if (replace) {
            params.set(key, value);
        } else {
            params.append(key, value);
        }
    }
    return params;
}

class VoxitAdminConversationFrontend extends VoxitAdminFrontend {
    conversations = new Map();
    tableElement;

    constructor(client) {
        super(client);

        this.statementEditor = new VoxitStatementEditor();
        this.statementEditor.dialog.addEventListener("command", this);
        this.statementEditor.dialog.addEventListener("submitstatement", this);

        this.statusOpenTemplateElement = document.querySelector("#status-open-template");
        this.statusClosedTemplateElement = document.querySelector("#status-closed-template");

        this.rowTemplateElement = document.querySelector("#conversation-row-template");

        const tableTemplateElement = document.querySelector("#conversations-table-template");
        const clone = document.importNode(tableTemplateElement.content, true);
        this.tableElement = clone.querySelector("#conversations-table");
        this.tableElement.addEventListener("click", this);

        document.querySelector("main").replaceChildren(clone);
    }

    async init() {
        this.updateConversationsList();
    }

    async onLogin() {
        this.updateConversationsList();
    }

    async updateConversationsList() {
        try {
            this.conversations = await this.client.fetchConversations();
        } catch (error) {
            this.handleClientError(error);
            console.error("Failed to update conversations:", error);
            return;
        }

        const rows = [];
        for (const conversation of this.conversations.values()) {
            const clone = document.importNode(this.rowTemplateElement.content, true);
            clone.querySelector(".conversation-id").textContent = conversation.id;
            clone.querySelector(".conversation-id-button > button").value = conversation.id;
            clone.querySelector(".conversation-topic").textContent = conversation.topic;
            const timeElement = clone.querySelector(".conversation-published time");
            timeElement.dateTime = conversation.created;
            timeElement.textContent = conversation.created.toLocaleDateString("en");
            let statusElement;
            if (conversation.isActive) {
                statusElement = document.importNode(this.statusOpenTemplateElement.content, true);
            } else {
                statusElement = document.importNode(this.statusClosedTemplateElement.content, true);
            }
            clone.querySelector(".conversation-status").appendChild(statusElement);
            for (const buttonElement of clone.querySelectorAll("button[command^='--open'],button[type='submit']")) {
                buttonElement.value = conversation.id;
            }
            rows.push(clone);
        }

        this.tableElement.querySelector("tbody").replaceChildren(...rows);
    }

    async onConversationIDCopyClick(id) {
        await navigator.clipboard.writeText(id);
    }

    async onStatementSubmit(conversationID, statement) {
        try {
            await this.client.addStatement(conversationID, statement);
        } catch(error) {
            if (error instanceof voxitAdminWebClient.StatementExistsError) {
                this.statementEditor.showStatementExistsError();
                return;
            } else {
                this.handleClientError(error);
                console.error("Failed to add statement:", error);
            }
        }

        this.statementEditor.close();
    }

    openStatementEditor(conversationID) {
        this.statementEditor.open(conversationID);
    }

    async handleEvent(event) {
        console.debug(event);
        switch (event.type) {
        case "click":
            if (event.target.classList.contains("copy-button")) {
                await this.onConversationIDCopyClick(event.target.value);
            }
            break;
        case "command":
            if (event.command === "--open-statement-editor") {
                event.preventDefault();

                this.openStatementEditor(event.source.value);
            }
            break;
        case "submitstatement":
            await this.onStatementSubmit(event.detail.conversationID, event.detail.statement);
            break;
        }
        super.handleEvent(event);
    }
}

class VoxitAdminModerationFrontend extends VoxitAdminFrontend {
    statements = new Map();
    tableElement;

    constructor(client, conversationID, statementsParams) {
        super(client);

        this.conversationID = conversationID;
        this.statementsParams = statementsParams;

        this.statementEditor = new VoxitStatementEditor();
        this.statementEditor.dialog.addEventListener("command", this);
        this.statementEditor.dialog.addEventListener("submitstatement", this);

        this.modStatusRejectedTemplateElement = document.querySelector("#mod-status-rejected-template");
        this.modStatusNoneTemplateElement = document.querySelector("#mod-status-none-template");
        this.modStatusAcceptedTemplateElement = document.querySelector("#mod-status-accepted-template");
        this.rowTemplateElement = document.querySelector("#statement-row-template");

        const tableTemplateElement = document.querySelector("#statements-table-template");
        const clone = document.importNode(tableTemplateElement.content, true);

        const filtersForm = clone.querySelector("#statements-filters");
        filtersForm["conversation_id"].value = this.conversationID;
        filtersForm["moderation_status"].value = this.statementsParams.get("moderation_status") ?? "none";
        filtersForm["text"].defaultValue = this.statementsParams.get("text") ?? "";
        filtersForm.addEventListener("change", this);
        filtersForm.addEventListener("submit", this);

        this.tableElement = clone.querySelector("#statements-table");
        this.tableElement.addEventListener("change", this);
        this.tableElement.addEventListener("submit", this);

        document.querySelector("main").replaceChildren(clone);

        // form associated element requires the form to be part of the document
        // first
        filtersForm["order"].value = this.statementsParams.get("order") === "desc" ? "desc" : "asc";
    }

    async init() {
        this.updateStatementsList();
    }

    async onLogin() {
        this.updateStatementsList();
    }

    createStatementRow(statement) {
        const row = document.importNode(this.rowTemplateElement.content, true)
        row.querySelector(".statement-id").textContent = statement.id;
        row.querySelector(".statement-text").textContent = statement.text;
        const timeElement = row.querySelector(".statement-added time");
        timeElement.dateTime = statement.created;
        timeElement.textContent = statement.created.toLocaleDateString("en");
        let statusElement;
        switch (statement.moderationStatus) {
        case voxitAdminWebClient.ModerationStatus.Rejected:
            statusElement = document.importNode(this.modStatusRejectedTemplateElement.content, true);
            break;
        case voxitAdminWebClient.ModerationStatus.None:
            statusElement = document.importNode(this.modStatusNoneTemplateElement.content, true);
            break;
        case voxitAdminWebClient.ModerationStatus.Accepted:
            statusElement = document.importNode(this.modStatusAcceptedTemplateElement.content, true);
            break;
        }
        row.querySelector(".statement-mod-status").appendChild(statusElement);
        const moderationFormElement = row.querySelector(".statement-actions .moderation-form");
        moderationFormElement["statement_id"].value = statement.id;
        moderationFormElement["moderation_status"].value = statement.moderationStatus;
        for (const buttonElement of row.querySelectorAll("button[command^='--open']")) {
            buttonElement.value = statement.id;
        }

        return row;
    }

    async updateStatementsList() {
        let response;
        try {
            response = await this.client.fetchStatements(this.conversationID, this.statementsParams);
        } catch (error) {
            this.handleClientError(error);
            console.error("Failed to update conversations:", error);
            return;
        }

        this.statements = new Map();
        const rows = [];
        for (const statement of response.data) {
            this.statements.set(statement.id, statement);
            const row = this.createStatementRow(statement);
            rows.push(row);
        }

        this.tableElement.querySelector("tbody").replaceChildren(...rows);

        const params = new URLSearchParams(document.location.search);
        const firstPageURL = response.paginationLinks.firstPageURL?.searchParams;
        const firstPageParams = typeof firstPageURL !== "undefined" ? mergeSearchParams(params, firstPageURL) : null;
        firstPageParams.delete("cursor");
        const firstLinkElement = document.querySelector("#first-page-link");
        firstLinkElement.hidden = firstPageParams === null;
        firstLinkElement.href = `?${firstPageParams}` ?? "";

        const nextPageURL = response.paginationLinks.nextPageURL?.searchParams;
        const nextPageParams = typeof nextPageURL !== "undefined" ? mergeSearchParams(params, nextPageURL) : null;
        const nextLinkElement = document.querySelector("#next-page-link");
        nextLinkElement.hidden = nextPageParams === null;
        nextLinkElement.href = `?${nextPageParams}` ?? "";
    }

    async onStatementSubmit(conversationID, statement) {
        try {
            await this.client.updateStatement(conversationID, statement);
        } catch(error) {
            if (error instanceof voxitAdminWebClient.StatementExistsError) {
                this.statementEditor.showStatementExistsError();
                return;
            } else {
                this.handleClientError(error);
                console.error("Failed to edit statement:", error);
            }
        }

        this.statementEditor.close();

        this.updateStatementsList();
    }

    openStatementEditor(conversationID, statementID) {
        const statement = this.statements.get(statementID);
        this.statementEditor.open(conversationID, statement);
    }

    async onModerationSubmit(statementID, moderationStatus) {
        const statement = this.statements.get(statementID);
        statement.moderationStatus = moderationStatus;

        try {
            this.client.updateStatement(conversationID, statement);
        } catch(error) {
            this.handleClientError(error);
            console.error("Failed to update statement:", error);
        }

        const row = this.createStatementRow(statement);
        this.tableElement.querySelector(`tr:has(button[value="${statementID}"])`).replaceWith(row);
    }

    async handleEvent(event) {
        console.debug(event);
        switch (event.type) {
        case "change":
            if (event.target.form?.id === "statements-filters" &&
                event.target.name !== "text") {
                const form = event.target.form;
                // submission via the order button should not submit a
                // potentially changed filter text
                form["text"].value = form["text"].defaultValue;
                form.submit();
            }
            break;
        case "submit":
            if (event.target.classList.contains("moderation-form")) {
                event.preventDefault();

                const data = new FormData(event.target, event.submitter);
                const statementID = Number(data.get("statement_id"));
                const moderationStatus = data.get("moderation_status");
                await this.onModerationSubmit(statementID, moderationStatus);
            }
            break;
        case "command":
            if (event.command === "--open-statement-editor") {
                event.preventDefault();

                this.openStatementEditor(this.conversationID, Number(event.source.value));
            }
            break;
        case "submitstatement":
            await this.onStatementSubmit(event.detail.conversationID, event.detail.statement);
            break;
        }
        super.handleEvent(event);
    }
}

const baseURL = typeof DEFAULT_BASE_URL !== "undefined" ? DEFAULT_BASE_URL : "";
const client = new voxitAdminWebClient.AdminClient(baseURL);
const urlParams = new URLSearchParams(window.location.search);
const conversationID = urlParams.get("conversation_id") ?? "";
if (!conversationID) {
    const app = new VoxitAdminConversationFrontend(client);
    app.init();
} else {
    const params = new voxitAdminWebClient.StatementsParams(urlParams);
    const app = new VoxitAdminModerationFrontend(client, conversationID, params);
    app.init();
}
